import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '@ajat/design-system/Header';
import SchoolSelector from './SchoolSelector';
import EditOrderLogin from '@ajat/design-system/EditOrderLogin';
import { useCart } from '../../../../context/CartContext';
import useHeaderMenuIntent from '../../../../hooks/useHeaderMenuIntent';
import useMenuItemVisibility from '../../../../hooks/useMenuItemVisibility';
import useBrandSettings from '../../../../hooks/useBrandSettings';
import useBrandSeo from '../../../../hooks/useBrandSeo';
import { readFreshSelection } from '../../../../services/schoolSelectionTtl';
import { loginToOrder } from '../../Common/EditOrder/ordersApi';

/* ══════════════════════════════════════════════════════════════════════════
 * CLS-SE — Common Header (TopBar + Header)
 * Single source of truth for header configuration across all CLS-SE pages
 * ══════════════════════════════════════════════════════════════════════════ */

const buildMobileSettings = (onMenuItemClick, showMenu1Promo = true) => ({
  mobileTitle: 'Menu',
  // The promo card is tied to menu_1 — hide it when the item itself is hidden
  // (open shop disabled + no menu_1 shop for the customer's school).
  ...(showMenu1Promo ? {
    mobilePromo: {
      cardType: 'button',
      image: require('../../../../assets/abc-se/menu/menu-item-9.jpg'),
      imageAlt: 'Buy skolmerch',
      label: 'KÖP SKOLMERCH',
      href: '#',
      onClick: (e) => { e.preventDefault(); onMenuItemClick('menu_1'); },
    },
  } : {}),
});

// Built per instance so "Skolmerch" (menu_1) and "Overalls & More" (menu_2)
// resolve to the customer's mapped school shop via onMenuItemClick.
// Flat, non-expanding nav — other menu items and mega-menus hidden for now.
// `menuKey` tags the two fixed entries so the header can hide one under the
// "Disable Open Shop" flag (useMenuItemVisibility); untagged entries stay.
const buildNavItems = (onMenuItemClick) => [
  {
    label: 'Skolmerch',
    menuKey: 'menu_1',
    href: '#',
    onClick: (e) => { e.preventDefault(); onMenuItemClick('menu_1'); },
  },
  {
    label: 'Overalls & More',
    menuKey: 'menu_2',
    href: '#',
    onClick: (e) => { e.preventDefault(); onMenuItemClick('menu_2'); },
  },
  {
    label: 'Kundservice',
    href: '/clothing/cls-se/faq',
  },
  {
    label: 'Studentmössor',
    href: 'https://clseifert.com/sv',
  },
];

const topBarNotice = '50.000+ ELEVER VÄLJER OSS VARJE ÅR';

const topBarLinks = [
  { label: 'FAQ', href: '/clothing/cls-se/faq' },
];

const CLSSEHeader = ({ showTopBar = true, hideOnScroll = true, onSchoolClick, ...headerProps }) => {
  const navigate = useNavigate();
  // Sibling routes that share one route element (e.g. /shop/:menuSlug) keep
  // this Header mounted, so the DS component needs a navigation signal to
  // close its open menus. location.key changes on EVERY history entry —
  // including a menu click that re-navigates to the page you are already on.
  const location = useLocation();
  const cart = useCart('clsse');
  const { showSchoolSelector } = useBrandSettings('clsse');
  useBrandSeo('clsse');
  const [isSchoolSelectorOpen, setIsSchoolSelectorOpen] = useState(false);
  const [isEditOrderLoginOpen, setIsEditOrderLoginOpen] = useState(false);
  const [pendingNavigate, setPendingNavigate] = useState(null);

  // Menu-item → mapped-shop resolution for the two fixed header items.
  const {
    onMenuItemClick,
    readIntentFromEvent,
    clearIntent,
    preferredSeasonResolver,
    navigateForIntentAfterSelect,
  } = useHeaderMenuIntent({ brandSlug: 'clsse', feSlug: 'cls-se' });

  // Hide a menu item when the open shop is disabled and the customer's school
  // has no open shop of that type (untagged entries always stay).
  const { isVisible } = useMenuItemVisibility('clsse');
  const navItems = useMemo(
    () => buildNavItems(onMenuItemClick).filter((i) => !i.menuKey || isVisible(i.menuKey)),
    [onMenuItemClick, isVisible],
  );
  const mobileSettings = useMemo(
    () => buildMobileSettings(onMenuItemClick, isVisible('menu_1')),
    [onMenuItemClick, isVisible],
  );

  // Show the selected school's name on the header school button (read fresh
  // each render — selecting/clearing re-renders via the selector state).
  const storedSchoolName = readFreshSelection('clsse_school_selection')?.school?.name || null;

  /* Build minicart items from cart context */
  const priceSuffix = ':-';
  const cartItems = [...cart.products, ...cart.packages].map((item) => {
    const opts = item.options || {};
    const details = [];
    if (opts.color) details.push(opts.color + (opts.size ? ` ${opts.size}` : ''));
    else if (opts.size) details.push(opts.size);
    if (opts.addons) {
      if (opts.addons.navnetryk) details.push(`Namntryck: ${opts.addons.navnetryk}`);
      if (opts.addons.lilleBrösttryck) details.push('Lille Brösttryck');
    }
    // Bonus products (server-appended, free) stay out of the minicart count.
    const visibleIncluded = (item.includedProducts || []).filter((ip) => !ip?.bonus);
    if (visibleIncluded.length) details.push(`${visibleIncluded.length} produkter inkluderade`);
    return {
      id: item.id,
      name: item.name,
      image: opts.image || item.image,
      details,
      quantity: item.quantity,
      price: `${Math.round(item.lineTotal)}${priceSuffix}`,
    };
  });

  useEffect(() => {
    // Selector disabled for the brand → never open the popup from the event.
    if (!showSchoolSelector) return undefined;
    const handleOpen = (e) => {
      setIsSchoolSelectorOpen(true);
      setPendingNavigate(e.detail?.navigateTo ?? null);
      readIntentFromEvent(e);
    };
    document.addEventListener('open-school-selector', handleOpen);
    return () => {
      document.removeEventListener('open-school-selector', handleOpen);
    };
  }, [showSchoolSelector, readIntentFromEvent]);

  const handleSchoolClick = () => {
    if (!showSchoolSelector) return;
    setIsSchoolSelectorOpen(true);
    setPendingNavigate(null);
    clearIntent();
    if (onSchoolClick) onSchoolClick();
  };

  const handleUserClick = () => {
    setIsEditOrderLoginOpen(true);
  };

  const handleNavigateAfterSelect = (selection) => {
    if (pendingNavigate) {
      const target = pendingNavigate;
      setPendingNavigate(null);
      navigate(target);
    } else if (selection?.school?.slug) {
      // Land directly in the chosen shop (school + season) when it has a slug;
      // otherwise fall back to the whole-school shop.
      const base = `/clothing/cls-se/school-shop/${selection.school.slug}`;
      const shopSlug = selection?.season?.slug;
      navigate(shopSlug ? `${base}/${shopSlug}` : base);
    } else {
      navigate('/clothing/cls-se/school-shop');
    }
  };

  return (
    <>
      <Header
        brand="CLS"
        subBrand="main"
        schoolLabel={storedSchoolName || 'VÄLJ SKOLA'}
        mode="default"
        showTopBar={showTopBar}
        topBarNotice={topBarNotice}
        topBarLinks={topBarLinks}
        hideOnScroll={hideOnScroll}
        navItems={navItems}
        mobileSettings={mobileSettings}
        routeKey={location.key}
        onSchoolClick={handleSchoolClick}
        showSchoolButton={showSchoolSelector}
        cartCount={cart.itemCount}
        cartItems={cartItems}
        cartTotal={`${Math.round(cart.total)}:-`}
        cartItemsLabel="Varor"
        cartTotalLabel="Total"
        cartCtaLabel="Till Kassan"
        onCartCtaClick={() => navigate('/clothing/cls-se/shopping-cart')}
        showUserButton
        orderLabel="Ändra beställning"
        onUserClick={handleUserClick}
        {...headerProps}
      />
      <SchoolSelector
        isOpen={showSchoolSelector && isSchoolSelectorOpen}
        onClose={() => {
          setIsSchoolSelectorOpen(false);
          setPendingNavigate(null);
          // Dismissing just closes the popup; drop the pending menu intent.
          clearIntent();
        }}
        onSelect={(selection) => {
          setIsSchoolSelectorOpen(false);
          // A pending menu intent navigates to the mapped shop (or fallback).
          if (navigateForIntentAfterSelect(selection)) return;
          handleNavigateAfterSelect(selection);
        }}
        preferredSeasonResolver={preferredSeasonResolver}
        onNotFoundClick={() => {
          setPendingNavigate(null);
          navigate('/clothing/cls-se/contact');
        }}
        notFoundTitle="Kan du ikke finde din skole?"
      />
      <EditOrderLogin
        onLogin={loginToOrder}
        isOpen={isEditOrderLoginOpen}
        onClose={() => setIsEditOrderLoginOpen(false)}
        apiBrandSlug="clsse"
        onSuccess={({ orderNumber, token, order }) => {
          setIsEditOrderLoginOpen(false);
          navigate(`/clothing/cls-se/edit-order/${orderNumber}`, { state: { token, order } });
        }}
        notFoundMessage="Vi kunde inte hitta någon beställning med den e-postadressen och det ordernumret."
        title="Ändra beställning"
        infoText="Du hittar ditt ordernummer i bekräftelsemejlet vi har skickat till dig."
        emailLabel="E-post"
        orderNumberLabel="Ordernummer"
        submitLabel="Fortsätt"
        emailErrorMessage="Ange en giltig e-postadress"
        orderNumberErrorMessage="Ange ett giltigt ordernummer"
      />
    </>
  );
};

export default CLSSEHeader;
